/**
 * PocketFiler - Feedback Widget
 * Floating feedback button with slide-out form on every page.
 */

import React, { useState, useEffect } from 'react';
import { MessageSquare, X, Bug, Lightbulb, MessageCircle, Send, Loader2, ChevronUp } from 'lucide-react';
import { useTranslation } from '../i18n';
import api from '../lib/api';
import { toast } from 'sonner';

// Capture console errors globally
if (typeof window !== 'undefined' && !window.__pf_recent_errors) {
  window.__pf_recent_errors = [];
  const origError = window.onerror;
  window.onerror = (msg, src, line, col, err) => {
    window.__pf_recent_errors.push({
      message: String(msg).slice(0, 200),
      source: src,
      line,
      timestamp: new Date().toISOString()
    });
    if (window.__pf_recent_errors.length > 10) window.__pf_recent_errors.shift();
    if (origError) origError(msg, src, line, col, err);
  };
}

const TYPE_OPTIONS = [
  { value: 'bug', icon: Bug, color: 'text-red-500' },
  { value: 'feature', icon: Lightbulb, color: 'text-amber-500' },
  { value: 'general', icon: MessageCircle, color: 'text-blue-500' },
];

const PRIORITY_OPTIONS = ['low', 'medium', 'high'];

export default function FeedbackWidget() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState('bug');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!description.trim() || description.trim().length < 5) {
      toast.error(t('feedback.tooShort') || 'Please provide more detail (at least 5 characters)');
      return;
    }

    setSubmitting(true);
    try {
      const ua = navigator.userAgent;
      await api.post('/feedback', {
        type,
        description: description.trim(),
        priority,
        page_url: window.location.pathname,
        browser: ua.slice(0, 200),
        screen_size: `${window.innerWidth}x${window.innerHeight}`,
        console_errors: (window.__pf_recent_errors || []).map(e => e.message).slice(-5),
      });
      toast.success(t('feedback.thanks') || 'Thanks! We\'ll look into this.');
      setDescription('');
      setType('bug');
      setPriority('medium');
      setOpen(false);
    } catch (e) {
      if (e.message?.includes('429')) {
        toast.error(t('feedback.rateLimit') || 'Too many submissions. Please wait a bit.');
      } else {
        toast.error(t('feedback.error') || 'Failed to submit feedback');
      }
    }
    setSubmitting(false);
  };

  // Don't show on login/signup
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  if (['/login', '/signup', '/verify-email'].some(p => path.startsWith(p))) {
    return null;
  }

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary text-primary-foreground shadow-lg hover:shadow-xl transition-all hover:scale-105 text-sm font-medium no-print"
        >
          <MessageSquare className="h-4 w-4" />
          {t('feedback.button') || 'Feedback'}
        </button>
      )}

      {/* Slide-out panel */}
      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-80 bg-white dark:bg-gray-900 rounded-xl shadow-2xl border overflow-hidden no-print animate-in slide-in-from-bottom-4">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50 dark:bg-gray-800">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-primary" />
              {t('feedback.title') || 'Send Feedback'}
            </h3>
            <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <X className="h-4 w-4" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-4 space-y-3">
            {/* Type selector */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                {t('feedback.type') || 'Type'}
              </label>
              <div className="flex gap-2">
                {TYPE_OPTIONS.map((opt) => {
                  const Icon = opt.icon;
                  const labels = {
                    bug: t('feedback.typeBug') || 'Bug',
                    feature: t('feedback.typeFeature') || 'Feature',
                    general: t('feedback.typeGeneral') || 'General',
                  };
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setType(opt.value)}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-lg border text-xs font-medium transition-colors ${
                        type === opt.value
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      <Icon className={`h-3.5 w-3.5 ${type === opt.value ? 'text-primary' : opt.color}`} />
                      {labels[opt.value]}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                {t('feedback.description') || 'Description'}
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('feedback.descPlaceholder') || 'Tell us what happened...'}
                rows={4}
                className="w-full rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-white dark:bg-gray-900"
                maxLength={5000}
                required
              />
            </div>

            {/* Priority */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                {t('feedback.priority') || 'Priority'}
              </label>
              <div className="flex gap-2">
                {PRIORITY_OPTIONS.map((p) => {
                  const labels = {
                    low: t('feedback.priorityLow') || 'Low',
                    medium: t('feedback.priorityMedium') || 'Medium',
                    high: t('feedback.priorityHigh') || 'High',
                  };
                  const colors = {
                    low: 'border-green-300 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-400',
                    medium: 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-400',
                    high: 'border-red-300 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400',
                  };
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPriority(p)}
                      className={`flex-1 py-1 px-2 rounded-md border text-xs font-medium transition-colors ${
                        priority === p ? colors[p] : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      {labels[p]}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Auto-captured info hint */}
            <p className="text-[10px] text-muted-foreground">
              {t('feedback.autoCapture') || 'Page URL, browser info, and error logs are captured automatically.'}
            </p>

            {/* Submit */}
            <button
              type="submit"
              disabled={submitting || !description.trim()}
              className="w-full flex items-center justify-center gap-2 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              {submitting ? (
                <><Loader2 className="h-3.5 w-3.5 animate-spin" /> {t('feedback.submitting') || 'Submitting...'}</>
              ) : (
                <><Send className="h-3.5 w-3.5" /> {t('feedback.submit') || 'Submit'}</>
              )}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
